import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  trackEvent,
  TrackedEvent,
  type MemAgentOverlayEntryClickEventParams,
  type MemAgentOverlayEntryPoint,
  type MemAgentOverlayFilterKind,
  type MemAgentOverlayItemKind,
  type MemAgentOverlayItemSource,
  type MemAgentOverlaySurfaceContextEventParams,
  type MemAgentOverlayTaskStatusAction,
} from "@/domains/metrics";
import { useAppStore } from "@/store";
import type { AgentOverlayState } from "@/store/agent-overlay/types";
import { AgentSurfaceId } from "@/store/agent-overlay/types";

export const trackAgentOverlayEntryClick = ({
  entryPoint,
  isSummaryLoaded,
  taskCount,
  projectCount,
}: {
  entryPoint: MemAgentOverlayEntryPoint;
  isSummaryLoaded: boolean;
  taskCount: number;
  projectCount: number;
}) => {
  const eventParams: MemAgentOverlayEntryClickEventParams = isSummaryLoaded
    ? {
        entry_point: entryPoint,
        task_count: taskCount,
        project_count: projectCount,
      }
    : { entry_point: entryPoint };

  trackEvent(TrackedEvent.MemAgentOverlayEntryClick, eventParams);
};

export const buildAgentOverlaySurfaceContext = (
  activeSurface: AgentOverlayState
): MemAgentOverlaySurfaceContextEventParams | null => {
  switch (activeSurface.surfaceId) {
    case AgentSurfaceId.Overview:
      return null;
    case AgentSurfaceId.Project:
    case AgentSurfaceId.ProjectTasks:
      return {
        surface_id: activeSurface.surfaceId,
        project_id: activeSurface.projectId,
      };
    case AgentSurfaceId.Task:
      return {
        surface_id: activeSurface.surfaceId,
        task_id: activeSurface.taskId,
      };
    default:
      return { surface_id: activeSurface.surfaceId };
  }
};

export const useAgentOverlayLifecycleAnalytics = ({
  activeSurface,
  isOpen,
}: {
  activeSurface: AgentOverlayState;
  isOpen: boolean;
}) => {
  const sessionStartedAtRef = useRef<number | null>(null);
  const lastViewedContextRef = useRef<MemAgentOverlaySurfaceContextEventParams | null>(null);
  const lastViewedContextKeyRef = useRef<string | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      if (
        wasOpenRef.current &&
        sessionStartedAtRef.current !== null &&
        lastViewedContextRef.current
      ) {
        trackEvent(TrackedEvent.MemAgentOverlayClose, {
          ...lastViewedContextRef.current,
          session_duration_ms: Date.now() - sessionStartedAtRef.current,
        });
      }

      sessionStartedAtRef.current = null;
      lastViewedContextRef.current = null;
      lastViewedContextKeyRef.current = null;
      wasOpenRef.current = false;
      return;
    }

    if (!wasOpenRef.current) {
      sessionStartedAtRef.current = Date.now();
      wasOpenRef.current = true;
    }

    const surfaceContext = buildAgentOverlaySurfaceContext(activeSurface);
    if (!surfaceContext) return;

    const surfaceContextKey = JSON.stringify(surfaceContext);
    if (surfaceContextKey === lastViewedContextKeyRef.current) return;

    trackEvent(TrackedEvent.MemAgentOverlayView, surfaceContext);
    lastViewedContextRef.current = surfaceContext;
    lastViewedContextKeyRef.current = surfaceContextKey;
  }, [activeSurface, isOpen]);
};

type TrackFilterChangeParams =
  | {
      filterKind: Exclude<MemAgentOverlayFilterKind, "search">;
      filterValue: string;
    }
  | {
      filterKind: "search";
      queryLength: number;
    };

interface TrackItemClickParams {
  itemId: string;
  itemKind: MemAgentOverlayItemKind;
  source: MemAgentOverlayItemSource;
}

interface TrackTaskStatusToggleParams {
  action: MemAgentOverlayTaskStatusAction;
  taskId: string;
}

interface AgentOverlayAnalytics {
  trackFilterChange: (params: TrackFilterChangeParams) => void;
  trackItemClick: (params: TrackItemClickParams) => void;
  trackTaskStatusToggle: (params: TrackTaskStatusToggleParams) => void;
}

export const useAgentOverlayAnalytics = (): AgentOverlayAnalytics => {
  const { store } = useAppStore();
  const activeSurface = store.agentOverlay.activeSurface;
  const surfaceContext = useMemo(
    () => buildAgentOverlaySurfaceContext(activeSurface),
    [activeSurface]
  );

  const trackFilterChange = useCallback(
    (params: TrackFilterChangeParams) => {
      if (!surfaceContext) return;

      if (params.filterKind === "search") {
        trackEvent(TrackedEvent.MemAgentOverlayFilterChange, {
          ...surfaceContext,
          filter_kind: params.filterKind,
          query_length: params.queryLength,
        });
        return;
      }

      trackEvent(TrackedEvent.MemAgentOverlayFilterChange, {
        ...surfaceContext,
        filter_kind: params.filterKind,
        filter_value: params.filterValue,
      });
    },
    [surfaceContext]
  );

  const trackItemClick = useCallback(
    ({ itemId, itemKind, source }: TrackItemClickParams) => {
      if (!surfaceContext) return;

      trackEvent(TrackedEvent.MemAgentOverlayItemClick, {
        ...surfaceContext,
        item_id: itemId,
        item_kind: itemKind,
        source,
      });
    },
    [surfaceContext]
  );

  const trackTaskStatusToggle = useCallback(
    ({ action, taskId }: TrackTaskStatusToggleParams) => {
      if (!surfaceContext) return;

      trackEvent(TrackedEvent.MemAgentOverlayTaskStatusToggle, {
        ...surfaceContext,
        action,
        task_id: taskId,
      });
    },
    [surfaceContext]
  );

  return {
    trackFilterChange,
    trackItemClick,
    trackTaskStatusToggle,
  };
};
